// Backend hosts per VITE_API_ENV key (see README "Local development environment"
// and useConfig getDefault*). The keys are intended PROFILES, not a guarantee that
// every map follows them — services have per-map exceptions, so read each map for
// its actual host. Rough intent per key:
//   sandbox     — LOCAL development: localhost hosts you run yourself (the default
//                 when VITE_API_ENV is unset). Exception: POI has no local host, so
//                 it points at staging even in sandbox.
//   development — deployed dev/testing, NOT local. ACH is deployed there via
//                 CircleCI and Ventura points at QA (same Ventura QA database), so
//                 run the local frontend against them without standing the back
//                 ends up yourself. Exception: services without a deployed dev host
//                 (GraphQL, GeoLink) still map `development` to localhost.
//   qa          — deployed QA/staging. Exception: GeoLink qa is still localhost.
//   production  — deployed production. Exception: GraphQL production is a ventura-qa
//                 placeholder pending its own deploy (see the TODO below).
export const apiHostMap = {
  development: 'https://ventura-qa.thinknearhub.com',
  sandbox: 'http://localhost:3000',
  production: 'https://dashboard.thinknear.com',
  qa: 'https://ventura-qa.thinknearhub.com',
};

export const graphQLApiHostMap = {
  development: 'http://localhost:9292/graphql',
  sandbox: 'http://localhost:9292/graphql',
  // TODO: Update this to the right URLs after QA ad production deployment
  production: 'https://ventura-qa.thinknearhub.com',
  qa: 'https://ventura-qa.thinknearhub.com',
};

// One 360 deployment per env serves both the POI management API
// (useConfig's poiManagementApiHost) and the userManagement UI the SSO ID
// columns deep-link into (SsoIdCell), so both read this map.
export const platform360HostMap = {
  development: 'https://dev.360platform.inmarket.com',
  sandbox: 'https://staging.360platform.inmarket.com',
  // TODO: Update this to the right URLs after QA ad production deployment
  production: 'https://360platform.inmarket.com',
  qa: 'https://staging.360platform.inmarket.com',
};

export const geolinkAudienceCreationServiceApiHostMap = {
  development: 'http://localhost:8080',
  sandbox: 'http://localhost:8080',
  production: 'https://audience-creation-service.aud.inmarket.com',
  qa: 'http://localhost:8080',
};

export const achApiHostMap = {
  development: 'https://activation-control-hub-dev.im-spec-ops.inmarket.com',
  sandbox: 'http://localhost:8080',
  qa: 'https://activation-control-hub-stg.im-spec-ops.inmarket.com',
  production: 'https://activation-control-hub-prod.im-spec-ops.inmarket.com',
};

// Host resolution — the rule behind useConfig's getDefault* helpers, kept here
// so consumers outside the provider (SsoIdCell) can apply it too. Not in
// useConfig: setupTests.ts mocks that module, which would shadow these.

// Kept here rather than beside util/helpers' isLocalhostUrl so this module
// imports nothing and stays a leaf; isLocalhostUrl reads it back from here.
export const LOCALHOST_HOSTNAMES = ['localhost', '127.0.0.1', '::1', '[::1]'];

export const API_ENVS = ['development', 'sandbox', 'qa', 'production'] as const;
export type ApiEnv = (typeof API_ENVS)[number];
export type HostMap = Record<ApiEnv, string>;

export const isApiEnv = (raw: unknown): raw is ApiEnv =>
  typeof raw === 'string' && (API_ENVS as readonly string[]).includes(raw);

// `isProdBuild` is import.meta.env.PROD; DEV is its exact complement, so one
// flag covers both branches. `dev.`/`-qa.`/`staging.` hosts are production
// BUILDS but not production.
export const resolveHostForEnv = (
  hostMap: HostMap,
  params: { apiEnv?: unknown; isProdBuild: boolean; hostname: string }
): string => {
  const { apiEnv, isProdBuild, hostname } = params;
  if (isApiEnv(apiEnv)) {
    return hostMap[apiEnv];
  }
  // Membership is enough where isLocalhostUrl parses: this only ever sees a bare
  // hostname, which parsing is a no-op for.
  if (!isProdBuild && LOCALHOST_HOSTNAMES.includes(hostname)) {
    return hostMap.sandbox;
  }
  if (
    isProdBuild &&
    !hostname.includes('-qa.') &&
    !hostname.includes('staging.') &&
    !hostname.startsWith('dev.')
  ) {
    return hostMap.production;
  }
  return hostMap.qa;
};

// VITE_API_ENV is a local-dev convenience, honored only in a DEV build.
export const selectedApiEnv = (): ApiEnv | undefined => {
  if (!import.meta.env.DEV) return undefined;
  return isApiEnv(import.meta.env.VITE_API_ENV) ? (import.meta.env.VITE_API_ENV as ApiEnv) : undefined;
};

export const hostForCurrentEnv = (hostMap: HostMap): string =>
  resolveHostForEnv(hostMap, {
    apiEnv: selectedApiEnv(),
    isProdBuild: import.meta.env.PROD,
    hostname: window.location.hostname,
  });
